import { modal } from "@/component/modal";
import { Config } from "@/shared/config";

const defaultHeaders = {
  "Content-Type": "application/json",
};

const getInitialBaseUrl = (): string => {
  if (typeof window === "undefined") return Config.apiUrl;

  const params = new URLSearchParams(window.location.search);

  if (params.get("env")) {
    localStorage.setItem("env", params.get("env")!);
  }

  const env = localStorage.getItem("env");

  if (env) {
    switch (env) {
      case "staging":
        return Config.apiStagingUrl;
      case "dev":
        return Config.apiDevUrl;
      default:
        return Config.apiUrl;
    }
  } else if (process.env.NODE_ENV === "development") {
    return Config.apiDevUrl;
  } else {
    return Config.apiUrl;
  }
};

type ClientOptions = {
  shouldReauth?: boolean;
};

export class Client {
  baseUrl: string;

  private refreshTokenPromise: Promise<Response> | null = null;
  private shouldReauth: boolean;

  constructor(baseUrl: string, options: ClientOptions = {}) {
    this.baseUrl = baseUrl;
    this.shouldReauth = options.shouldReauth ?? true;
  }

  async fetch(
    url: string,
    init?: RequestInit,
  ): Promise<Response> {
    const headers = {
      ...defaultHeaders,
      ...(init?.headers || {}),
    };

    const uri = this.baseUrl + url;



    const response = await fetch(uri, {
      ...init,
      headers,
      credentials: "include",
    });
    const method = init?.method ?? "GET";
    const decodedUrl = decodeURIComponent(uri);

    console.log(
      `[Client] method: ${method}, request: ${decodedUrl}, response: ${response.status}`,
    );

    if (response.status === 401) {
      const clonedResponse = response.clone();
      const body = await clonedResponse.json();
      const error = body.error;

      console.log("[Client] 401", error);

      const reAuthErrors = [
        "token_invalid",
        "token_not_found",
        "user_not_found",
        "malformed_token",
      ];

      // Token expired
      const tokenExpired = error === "token_expired";
      if (tokenExpired) {
        if (!this.refreshTokenPromise) {
          this.refreshTokenPromise = fetch(
            this.baseUrl + "/auth/refresh-token",
            {
              method: "POST",
              headers: headers,
              credentials: "include",
            },
          );
        }

        const refreshTokenResponse = await this.refreshTokenPromise;

        this.refreshTokenPromise = null;

        if (refreshTokenResponse.ok) {
          return await this.fetch(url, {
            ...init,
            headers,
            credentials: "include",
          });
        } else {
          this.refreshTokenPromise = null;

          this.reauth();
          return response;
        }
      }

      // Token invalid -> reauth
      const reauth = reAuthErrors.includes(error ?? "");
      if (reauth) {
        this.reauth();
        return response;
      }

      // Generic forbidden error
      const message = body.message || body.error;

      modal.alert("Can't get response from the server", {
        message: message ?? response.statusText,
      });
    } else if (!response.ok) {
      const clonedResponse = response.clone();
      const body = await clonedResponse.json();
      const message = body.message || body.error;

      modal.alert("Can't get response from the server", {
        message: message ?? response.statusText,
      });
      throw new Error(message ?? response.statusText);
    }

    return response;
  }

  private async reauth(message: string = "Session expired, please login again") {
    if (!this.shouldReauth) return;

    const confirmed = await modal.confirm(
      message,
    );

    if (confirmed) {
      this.logout();
    }
  }

  public async logout(target: string = "/auth") {
    this.fetch("/auth/logout", {
      method: "POST",
    }).finally(() => {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = target;
    });
  }

  /**
   * Upload a file using multipart/form-data. Do NOT set Content-Type header; browser will set it.
   * @param url - The endpoint URL (relative to baseUrl)
   * @param formData - The FormData object containing file and fields
   * @param options - Optional fetch options
   */
  async upload(
    url: string,
    formData: FormData,
  ): Promise<Response> {
    const uri = this.baseUrl + url;
    let response = await fetch(uri, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    if (response.status === 401) {
      const clonedResponse = response.clone();
      let body;
      try {
        body = await clonedResponse.json();
      } catch {
        body = {};
      }
      const error = body.error;
      const reAuthErrors = [
        "token_invalid",
        "token_not_found",
        "user_not_found",
        "malformed_token",
      ];
      const tokenExpired = error === "token_expired";
      if (tokenExpired) {
        if (!this.refreshTokenPromise) {
          this.refreshTokenPromise = fetch(
            this.baseUrl + "/auth/refresh-token",
            {
              method: "POST",
              credentials: "include",
            },
          );
        }
        const refreshTokenResponse = await this.refreshTokenPromise;
        this.refreshTokenPromise = null;
        if (refreshTokenResponse.ok) {
          // Retry the upload once
          response = await fetch(uri, {
            method: "POST",
            body: formData,
            credentials: "include",
          });
        } else {
          this.reauth();
          return response;
        }
      } else if (reAuthErrors.includes(error ?? "")) {
        this.reauth();
        return response;
      }
    }

    return response;
  }

  async streamFetch(
    url: string,
    init: RequestInit,
    onChunk: (chunk: string) => void,
  ): Promise<void> {
    const headers = {
      ...defaultHeaders,
      ...(init?.headers || {}),
    };
    const uri = this.baseUrl + url;

    const response = await fetch(uri, {
      ...init,
      headers,
      credentials: "include",
    });

    const method = init?.method ?? "GET";
    const decodedUrl = decodeURIComponent(url);

    console.log(
      `[Client] stream method: ${method}, request: ${decodedUrl}, response: ${response.status}`,
    );


    if (response.status === 401) {
      const clonedResponse = response.clone();
      let body;
      try {
        body = await clonedResponse.json();
      } catch {
        // If the response is not JSON, it might be an empty 401 or other format
        body = { error: "token_expired", message: "Unauthorized" };
      }
      const error = body.error;
      const reAuthErrors = [
        "token_invalid",
        "token_not_found",
        "user_not_found",
        "malformed_token",
      ];

      const tokenExpired = error === "token_expired";
      if (tokenExpired) {
        if (!this.refreshTokenPromise) {
          this.refreshTokenPromise = fetch(
            this.baseUrl + "/auth/refresh-token",
            {
              method: "POST",
              headers: headers,
              credentials: "include",
            },
          );
        }
        const refreshTokenResponse = await this.refreshTokenPromise;
        this.refreshTokenPromise = null;

        if (refreshTokenResponse.ok) {
          // Retry the stream fetch once
          return this.streamFetch(url, init, onChunk);
        } else {
          this.reauth(body.message || "Session expired, please login again");
          throw new ApiException(error, body.message || "Session expired");
        }
      } else if (reAuthErrors.includes(error ?? "")) {
        this.reauth(body.message || "Invalid session, please login again");
        throw new ApiException(error, body.message || "Invalid session");
      } else {
        const message = body.message || body.error || response.statusText;
        modal.alert(message);
        throw new ApiException(error, message);
      }
    }

    if (!response.ok) {
      const clonedResponse = response.clone();
      let body;
      try {
        body = await clonedResponse.json();
      } catch {
        body = {};
      }
      const message = body.message || body.error || response.statusText;
      modal.alert(message);
      throw new Error(message);
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        const chunk = decoder.decode(value, { stream: true });

        // SSE streams send data like "data: {...}\n\n"
        // We need to parse this
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const jsonData = line.substring("data: ".length);
            if (jsonData.trim()) { // Ensure it's not just whitespace
              try {
                onChunk(JSON.parse(jsonData));
              } catch (e) {
                // If JSON.parse fails, it might be a non-JSON string chunk
                // or the end of a JSON object that was split across chunks
                // For now, we'll send it as is, assuming the consumer can handle it
                // or it's part of a larger JSON object.
                // A more robust solution might involve buffering and attempting to parse.
                console.warn("[Client] streamFetch: Failed to parse JSON chunk, sending as raw:", jsonData, e);
                onChunk(jsonData);
              }
            }
          } else if (line.trim()) {
            // Handle non-SSE lines if necessary, or log them
            console.log("[Client] streamFetch: Received non-data line:", line);
          }
        }
      }
    } catch (error) {
      console.error("[Client] streamFetch: Error reading stream:", error);
      modal.alert("Error processing stream");
      throw error;
    } finally {
      reader.releaseLock();
    }
  }
}

export const client = new Client(getInitialBaseUrl(), { shouldReauth: false });

export class ApiException extends Error {
  code?: string;

  constructor(code?: string, message?: string) {
    super(message);
    this.code = code;
  }
}

if (typeof window !== "undefined") {
  window.beta = undefined;
  window.production = undefined;
  Object.defineProperty(window, "beta", {
    configurable: true,
    get: function () {
      client.baseUrl = "https://api.beta.vareselab.co";
      localStorage.setItem("apiEnv", "beta");
      console.log("Switched to beta API");
      return undefined;
    },
  });
  Object.defineProperty(window, "production", {
    configurable: true,
    get: function () {
      client.baseUrl = process.env.NEXT_PUBLIC_BACKEND_API_URL!;
      localStorage.setItem("apiEnv", "production");
      console.log("Switched to production API");
      return undefined;
    },
  });
}

declare global {
  interface Window {
    beta: any;
    production: any;
  }
}
